import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DualEventHero from "../components/DualEventHero";
import Ticker from "../components/Ticker";
import FlagshipWorldMap from "../components/FlagshipWorldMap";
import UpcomingXrpLedgerEvents from "../components/UpcomingXrpLedgerEvents";
import NewsletterSubscription from "../components/NewsletterSubscription";
import LegacyShowcaseScroller, { type LegacyShowcaseItem } from "../components/LegacyShowcaseScroller";
import SiteStructuredData from "../components/SiteStructuredData";
import useScrollToTop from "../hooks/useScrollToTop";
import { useMainPageGsap } from "../hooks/useMainPageGsap";
import {
  SITE_HOME_DESCRIPTION,
  SITE_HOME_KEYWORDS,
  SITE_HOME_TITLE,
} from "../constants/siteMeta";

function homeProofIconMaskStyle(assetFile: string): React.CSSProperties {
  const path = `/images/${assetFile}`;
  return {
    WebkitMaskImage: `url(${JSON.stringify(path)})`,
    maskImage: `url(${JSON.stringify(path)})`,
  };
}

export type MainPageProps = {
  isMobile: boolean;
  onTicketClick: () => void;
};

const MainPage: React.FC<MainPageProps> = ({ isMobile, onTicketClick }) => {
  useScrollToTop();
  const pastEventsSectionRef = useRef<HTMLElement>(null);
  const subHeroRef = useRef<HTMLElement>(null);
  const apacStatsSectionRef = useRef<HTMLElement>(null);
  const homeProofSectionRef = useRef<HTMLElement>(null);
  const homeAudienceSectionRef = useRef<HTMLElement>(null);
  const homeHeroContentRef = useRef<HTMLDivElement>(null);

  useMainPageGsap({
    subHeroRef,
    pastEventsSectionRef,
    apacStatsSectionRef,
    homeAudienceSectionRef,
    homeProofSectionRef,
  });

  const legacyShowcaseItems: LegacyShowcaseItem[] = [
    {
      title: "XRP Australia 2026",
      description:
        "A world-class event at Crown Sydney that showcased financial innovation and brought together industry leaders, developers, and the global web3 community.",
      imageSrc: "/images/legacy_australia_2026.png",
      imageAlt: "XRP Australia 2026 at Crown Sydney",
      to: "/sydney2026",
      ctaLabel: "View Event",
    },
    {
      title: "XRP Gold Coast 2024",
      description:
        "A world-class 3-day conference at Royal Pines Resort, Queensland, with 300+ attendees, 20 sponsors, and a comprehensive hackathon.",
      imageSrc: "/images/past_event_goldcoast.png",
      imageAlt: "XRP Gold Coast 2024 at Royal Pines Resort, Queensland",
    },
    {
      title: "Ledger Lounge",
      description:
        "A Singapore-based event featuring deep-dive discussions on blockchain security, governance, and emerging technologies.",
      imageSrc: "/images/past_event_lounge.png",
      imageAlt: "Ledger Lounge",
    },
    {
      title: "Bridging Blockchains",
      description:
        "Industry leaders gathered for key discussions on blockchain innovation with strategic networking sessions and collaborative dialogue.",
      imageSrc: "/images/brisbane_event_done.png",
      imageAlt: "Bridging Blockchains",
    },
  ];

  return (
    <div className="app-container app-container--home">
      <Helmet>
        <title>{SITE_HOME_TITLE}</title>
        <meta name="description" content={SITE_HOME_DESCRIPTION} />
        <meta property="og:title" content={SITE_HOME_TITLE} />
        <meta name="twitter:title" content={SITE_HOME_TITLE} />
        <meta property="og:image:alt" content={SITE_HOME_TITLE} />
        <meta name="twitter:image:alt" content={SITE_HOME_TITLE} />
        <meta name="keywords" content={SITE_HOME_KEYWORDS} />
        <meta property="og:updated_time" content="2026-07-25T17:50:00.000Z" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap"
        />
      </Helmet>
      <SiteStructuredData includeOrganization={false} includePastEvents />
      <Header onTicketClick={onTicketClick} />
      <main>
        <DualEventHero />
        <FlagshipWorldMap />
        <UpcomingXrpLedgerEvents />
        <Ticker
          title="Trusted By Industry Leaders"
          logos={[
            { src: "/sponsors/coinbase.webp", alt: "Coinbase", href: "https://www.coinbase.com" },
            { src: "/sponsors/okx.webp", alt: "OKX", href: "https://www.okx.com" },
            { src: "/sponsors/audd.webp", alt: "AUDD", href: "https://www.audd.digital" },
            { src: "/sponsors/flare.webp", alt: "Flare", href: "https://flare.xyz" },
            { src: "/sponsors/evernorth.webp", alt: "Evernorth", href: "https://evernorth.xyz" },
            { src: "/sponsors/gemini.webp", alt: "Gemini", href: "https://www.gemini.com" },
            { src: "/sponsors/yellow_logo.webp", alt: "Yellow", href: "https://www.yellow.org" },
            { src: "/sponsors/morgan_mac.webp", alt: "Morgan Mac", href: "https://www.morganmac.com.au" },
            { src: "/sponsors/ripple.webp", alt: "Ripple", href: "https://www.ripple.com" },
            { src: "/sponsors/xaman.webp", alt: "Xaman", href: "https://xaman.app" },
            { src: "/sponsors/independent_reserve.webp", alt: "Independent Reserve", href: "https://www.independentreserve.com" },
            { src: "/sponsors/cointelegraph.webp", alt: "Cointelegraph", href: "https://cointelegraph.com" },
            { src: "/sponsors/victoria_uni.webp", alt: "Victoria University", href: "https://www.vu.edu.au" },
          ]}
          speed={30}
          direction="left"
        />

        {/* PREVIEW LAYOUT: other homepage sections commented out — uncomment to restore
        <section
          id="home"
          className="section hero-section"
          style={{
            background: "#ffffff",
            position: "relative",
            overflow: "hidden",
            height: isMobile ? "100svh" : "calc(100svh - 60px)",
            minHeight: isMobile ? "100svh" : "calc(100svh - 60px)",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: "transparent",
              zIndex: 2,
            }}
          />
          <div className="home-hero-content" ref={homeHeroContentRef}>
            <p className="home-hero-kicker home-hero-animate">UK &amp; APAC · XRP Ledger Events</p>
            <h1 className="home-hero-title home-hero-animate">
              Connecting <span className="home-hero-kw home-hero-kw--dark">Communities</span>,{" "}
              <span className="home-hero-kw home-hero-kw--primary">Institutions</span>,{" "}
              <span className="home-hero-kw home-hero-kw--dark">Builders</span> and{" "}
              <span className="home-hero-kw home-hero-kw--primary">Regulators</span>.
            </h1>
            <p className="home-hero-subtitle home-hero-animate">
              Conferences and programs focused on the{" "}
              <span className="home-hero-kw home-hero-kw--primary">XRP Ledger</span>,{" "}
              <span className="home-hero-kw home-hero-kw--dark">digital assets</span>, and{" "}
              <span className="home-hero-kw home-hero-kw--primary">institutional finance</span> across the{" "}
              <span className="home-hero-kw home-hero-kw--dark">UK &amp; APAC</span>.
            </p>
            <div className="home-hero-actions home-hero-animate">
              <BrandGradientButton to="/#footer-register" className="home-hero-apply-button">
                Apply for the Program
              </BrandGradientButton>
              <Link to="/gallery" className="home-hero-secondary-cta">
                View 2026 Highlights
              </Link>
            </div>
          </div>
        </section>

        <div className="home-programs-sky-band">
          <section ref={subHeroRef} className="home-sub-hero-section" aria-labelledby="home-sub-hero-heading">
            <div className="home-sub-hero-content home-sub-hero-with-map">
              <div className="home-sub-hero-text">
                <h2 id="home-sub-hero-heading" className="home-sub-hero-title">
                  <span className="home-sub-hero-title-line">Wave of Innovation&apos;s</span>
                  <span className="home-sub-hero-title-accent">Sydney-London events</span>
                </h2>
                <p className="home-sub-hero-lede">
                  Through conferences and programmes in Sydney and London, we facilitate the network corridor between
                  these two financial hubs, putting builders, institutions, and communities in the same rooms across the
                  XRP Ledger.
                </p>
                <div className="home-sub-hero-cta-box">
                  <p className="home-sub-hero-cta-text">
                    See past flagships and register for updates on XRP Australia (18–19 March 2027) and XRP London (2–4 June 2027).
                  </p>
                  <Link to="/events" className="home-sub-hero-cta-button">
                    Explore our events
                  </Link>
                </div>
              </div>
              <div className="home-sub-hero-map-wrap" aria-hidden="true">
                <div className="home-sub-hero-map-panel">
                  <img
                    src="/images/uk_apac_network.png"
                    alt=""
                    className="home-sub-hero-apac-globe"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </section>

          <section ref={apacStatsSectionRef} className="apac-stats-section">
            <h2 className="apac-stats-title">Why we host events in Sydney and London:</h2>
            <div className="apac-stats-grid">
              <div className="apac-stat-block">
                <div className="apac-stat-value">#2</div>
                <div className="apac-stat-label">London</div>
                <div className="apac-stat-subtext">Global financial centre (GFCI 38)</div>
              </div>
              <div className="apac-stat-block">
                <div className="apac-stat-value">#23</div>
                <div className="apac-stat-label">Sydney</div>
                <div className="apac-stat-subtext">Rising APAC financial centre (GFCI 38)</div>
              </div>
              <div className="apac-stat-block">
                <div className="apac-stat-value">700</div>
                <div className="apac-stat-label">Attendees delivered</div>
                <div className="apac-stat-subtext">Across Gold Coast and Sydney events</div>
              </div>
            </div>
          </section>
        </div>

        <section ref={homeAudienceSectionRef} className="home-audience-section" aria-labelledby="home-audience-heading">
          <div className="home-audience-container">
            <h2 id="home-audience-heading" className="home-audience-title">
              Choose your starting point
            </h2>
            <p className="home-audience-subtitle">Tailored pathways for institutions, builders, and ecosystem partners.</p>
            <div className="home-audience-grid">
              <article className="home-audience-card home-audience-card--violet">
                <div className="home-audience-card-media">
                  <img
                    src="/images/institutions.webp"
                    alt="Modern glass tower viewed from street level, representing institutional scale"
                    loading="lazy"
                  />
                </div>
                <div className="home-audience-card-body">
                  <p className="home-audience-card-copy">
                    <strong>Institutions.</strong> Explore pilot-to-production support, policy alignment, and enterprise collaboration
                    tracks.
                  </p>
                  <Link to="/#footer-register" className="home-audience-card-link">
                    Start institutional pathway
                  </Link>
                </div>
              </article>
              <article className="home-audience-card home-audience-card--amber">
                <div className="home-audience-card-media">
                  <img
                    src="/images/builders.webp"
                    alt="Developers and attendees at an XRP Australia workshop with presentation screens"
                    loading="lazy"
                  />
                </div>
                <div className="home-audience-card-body">
                  <p className="home-audience-card-copy">
                    <strong>Builders.</strong> Access events, technical ecosystems, and partnerships that accelerate product deployment.
                  </p>
                  <Link to="/#footer-register" className="home-audience-card-link">
                    See builder opportunities
                  </Link>
                </div>
              </article>
              <article className="home-audience-card home-audience-card--teal">
                <div className="home-audience-card-media">
                  <img src="/images/sponsors.webp" alt="Event display featuring ecosystem partner branding" loading="lazy" />
                </div>
                <div className="home-audience-card-body">
                  <p className="home-audience-card-copy">
                    <strong>Sponsors &amp; partners.</strong> Get brand visibility, strategic deal flow, and direct access to
                    high-intent participants.
                  </p>
                  <Link to="/#footer-register" className="home-audience-card-link">
                    Become a partner
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section ref={homeProofSectionRef} className="home-proof-section" aria-labelledby="home-proof-heading">
          <div className="home-proof-container">
            <div className="home-proof-header-row">
              <h2 id="home-proof-heading" className="home-proof-title">
                Why teams partner with Wave of Innovation
              </h2>
              <Link to="/#footer-register" className="home-proof-header-link">
                Register interest
              </Link>
            </div>
            <div className="home-proof-grid">
              <article className="home-proof-card home-proof-card--violet">
                <div
                  className="home-proof-card-icon"
                  aria-hidden="true"
                  style={homeProofIconMaskStyle("Institution-first approach.svg")}
                />
                <p className="home-proof-card-fact">
                  Programs are designed so <span className="home-proof-highlight">institutional teams</span> sit in working sessions
                  with builders and policy voices, not just auditorium keynotes.
                </p>
              </article>
              <article className="home-proof-card home-proof-card--amber">
                <div
                  className="home-proof-card-icon"
                  aria-hidden="true"
                  style={homeProofIconMaskStyle("Institutional strategy leaders.svg")}
                />
                <p className="home-proof-card-fact">
                  Each wave pairs <span className="home-proof-highlight">hands-on technical tracks</span> with deal rooms and demos
                  so products move from concept toward deployment.
                </p>
              </article>
              <article className="home-proof-card home-proof-card--teal">
                <div
                  className="home-proof-card-icon"
                  aria-hidden="true"
                  style={homeProofIconMaskStyle("Trusted collaboration.svg")}
                />
                <p className="home-proof-card-fact">
                  Partner packages emphasize <span className="home-proof-highlight">qualified introductions</span> and repeat
                  touchpoints with attendees who are actively evaluating solutions.
                </p>
              </article>
            </div>
          </div>
        </section>

        <LegacyShowcaseScroller
          sectionRef={pastEventsSectionRef}
          id="past-events"
          heading="Our Legacy"
          introLines={[
            "Building the future of blockchain through impactful events and meaningful connections.",
            "Each conference and initiative represents our commitment to advancing the ecosystem and empowering the community. However, this is just the beginning.",
          ]}
          items={legacyShowcaseItems}
        />

        <NewsletterSubscription variant="panel" />
        */}
      </main>
      <Footer onTicketClick={onTicketClick} />
    </div>
  );
};

export default MainPage;
